<script lang="ts">
  import type { AdminEventConfig } from '../../lib/convexApi';
  import { createEventDispatcher } from 'svelte';

  export let events: AdminEventConfig[] = [];
  export let loading = false;
  export let error: string | null = null;
  export let selectedEventId: string | null = null;

  const dispatch = createEventDispatcher();

  function handleEdit(eventId: string) {
    dispatch('edit', { eventId });
  }

  function handleGuestlist(eventId: string) {
    dispatch('guestlist', { eventId });
  }

  function handlePreview(eventId: string) {
    dispatch('preview', { eventId });
  }
</script>

<section class="events-hub">
  <header class="events-header">
    <h2>Events</h2>
    <p class="subtitle">Configure, monitor, and open detailed views for each event.</p>
  </header>

  {#if loading}
    <div class="panel muted">Loading events…</div>
  {:else if error}
    <div class="panel error">{error}</div>
  {:else if events.length === 0}
    <div class="panel muted">No events yet. Create your first event to get started.</div>
  {:else}
    <div class="event-grid">
      {#each events as event (event.id)}
        <article class="event-card" class:selected={event.id === selectedEventId}>
          <header>
            <div class="badge">{new Date(event.schedule.start).toLocaleDateString()}</div>
            <h3>{event.name}</h3>
            <p>{event.headline}</p>
          </header>
          <dl class="meta">
            <div>
              <dt>Schedule</dt>
              <dd>{new Date(event.schedule.start).toLocaleString()} → {new Date(event.schedule.end).toLocaleString()}</dd>
            </div>
            <div>
              <dt>Ticket range</dt>
              <dd>{event.ticket.startPrice.toFixed(2)} → {event.ticket.endPrice.toFixed(2)} {event.ticket.currency.toUpperCase()}</dd>
            </div>
            <div>
              <dt>Extras</dt>
              <dd>{event.extras.length > 0 ? `${event.extras.length} configured` : 'None yet'}</dd>
            </div>
          </dl>
          <footer>
            <button type="button" on:click={() => handleGuestlist(event.id)}>Manage guestlist</button>
            <button type="button" class="ghost" on:click={() => handlePreview(event.id)}>Preview</button>
            <button type="button" class="ghost" on:click={() => handleEdit(event.id)}>Edit</button>
          </footer>
        </article>
      {/each}
    </div>
  {/if}
</section>

<style>
  .events-hub {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }

  .events-header h2 {
    font-size: 1.6rem;
    font-weight: 600;
  }

  .subtitle {
    color: var(--color-text-muted);
    margin: 0;
  }

  .panel {
    padding: var(--space-5);
    border-radius: var(--radius-xl);
    background: rgba(255, 255, 255, 0.04);
  }

  .panel.error {
    border: 1px solid rgba(255, 96, 96, 0.4);
    background: rgba(69, 14, 14, 0.35);
  }

  .panel.muted {
    color: var(--color-text-muted);
  }

  .event-grid {
    display: grid;
    gap: var(--space-4);
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  }

  .event-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-5);
    border-radius: var(--radius-xl);
    background:
      linear-gradient(140deg, rgba(16, 18, 26, 0.94), rgba(16, 18, 26, 0.8)),
      radial-gradient(120% 140% at 10% 0%, rgba(240, 169, 74, 0.25), transparent 55%);
    border: 1px solid rgba(255, 255, 255, 0.06);
    box-shadow: var(--shadow-md);
    transition: transform 180ms ease, box-shadow 180ms ease;
  }

  .event-card.selected {
    box-shadow: 0 0 0 2px rgba(240, 169, 74, 0.6);
  }

  .event-card header {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .event-card h3 {
    font-size: 1.25rem;
    font-weight: 600;
  }

  .badge {
    align-self: flex-start;
    padding: 0.2rem 0.8rem;
    border-radius: var(--radius-md);
    font-size: 0.75rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.12);
  }

  .meta {
    display: grid;
    gap: var(--space-3);
  }

  .meta dt {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-text-muted);
  }

  .meta dd {
    margin: 0;
  }

  footer {
    display: flex;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  footer button {
    flex: 1 0 140px;
    padding: 0.65rem 1rem;
    border-radius: var(--radius-lg);
    border: none;
    cursor: pointer;
    background: linear-gradient(135deg, rgba(240, 169, 74, 0.6), rgba(178, 127, 244, 0.4));
    color: #050608;
    font-weight: 600;
    transition: transform 150ms ease, box-shadow 150ms ease;
  }

  footer button:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(240, 169, 74, 0.35);
  }

  footer button.ghost {
    background: rgba(255, 255, 255, 0.08);
    color: var(--color-text-primary);
    box-shadow: none;
  }
</style>
